/**
 * Streaming Event Parser
 *
 * Parses Server-Sent Events (SSE) from workflow execution streaming endpoint.
 * Handles event parsing, validation, and state aggregation for UI display.
 */

/**
 * Phase configuration for the streaming workflow
 */
export const PHASES = {
  1: {
    id: 1,
    name: "Query Decomposition",
    description: "Breaking down user query into multiple intelligent search queries",
  },
  2: {
    id: 2,
    name: "Parallel Corpus Search",
    description: "Searching master and private document indexes in parallel",
  },
  3: {
    id: 3,
    name: "Global Reranking",
    description: "Ranking all search results by relevance",
  },
  4: {
    id: 4,
    name: "Comprehensive Analysis",
    description: "Generating comprehensive certification roadmap",
  },
  5: {
    id: 5,
    name: "Executive Summary",
    description: "Extracting 1-page actionable summary from comprehensive analysis",
  },
  6: {
    id: 6,
    name: "Web Validation",
    description: "Validating results against authoritative web sources",
  },
  7: {
    id: 7,
    name: "Task Generation",
    description: "Creating hierarchical tasks from analysis",
  },
};

/**
 * Event type constants
 */
export const EVENT_TYPES = {
  // Phase 1: Query Decomposition
  DECOMPOSITION_START: "decomposition_start",
  DECOMPOSITION_PROGRESS: "decomposition_progress",
  DECOMPOSITION_COMPLETE: "decomposition_complete",

  // Phase 2: Parallel Corpus Search
  SEARCH_START: "search_start",
  SEARCH_QUERY_PROGRESS: "search_query_progress",
  SEARCH_QUERY_COMPLETE: "search_query_complete",
  SEARCH_ALL_COMPLETE: "search_all_complete",

  // Phase 3: Global Reranking
  RERANK_START: "rerank_start",
  RERANK_PROGRESS: "rerank_progress",
  RERANK_COMPLETE: "rerank_complete",

  // Progress log tokens (phases 1-4 — shown in log panel)
  PROGRESS_TOKEN: "progress_token",

  // Phase 4: Streaming LLM Analysis
  ANALYSIS_START: "analysis_start",
  ANALYSIS_TOKEN: "analysis_token",
  ANALYSIS_COMPLETE: "analysis_complete",
  FULL_REPORT_TOKEN: "full_report_token",

  // Phase 4: Parallel agent events
  PARALLEL_AGENTS_START: "parallel_agents_start",
  PARALLEL_AGENTS_COMPLETE: "parallel_agents_complete",
  AGENT_START: "agent_start",
  AGENT_COMPLETE: "agent_complete",

  // Phase 4: Topic-based execution events
  TOPIC_DECOMPOSE_START: "topic_decompose_start",
  TOPIC_DECOMPOSE_COMPLETE: "topic_decompose_complete",
  TOPIC_AGENT_START: "topic_agent_start",
  TOPIC_AGENT_RETRY: "topic_agent_retry",
  TOPIC_AGENT_COMPLETE: "topic_agent_complete",
  TOPIC_ASSEMBLE_START: "topic_assemble_start",
  TOPIC_ASSEMBLE_COMPLETE: "topic_assemble_complete",

  // Phase 4: Comprehensive retrieval events
  COMPREHENSIVE_RETRIEVAL_START: "comprehensive_retrieval_start",
  COMPREHENSIVE_RETRIEVAL_COMPLETE: "comprehensive_retrieval_complete",

  // Phase 4: Completeness validation
  COMPLETENESS_VALIDATION_START: "completeness_validation_start",
  COMPLETENESS_VALIDATION_COMPLETE: "completeness_validation_complete",

  // Phase 5: Executive Summary (1-Page)
  EXECUTIVE_SUMMARY_START: "executive_summary_start",
  EXECUTIVE_SUMMARY_TOKEN: "executive_summary_token",
  EXECUTIVE_SUMMARY_COMPLETE: "executive_summary_complete",

  // Phase 5: Web Validation
  WEB_VALIDATION_START: "web_validation_start",
  WEB_VALIDATION_QUERY_REFINE: "web_validation_query_refine",
  WEB_VALIDATION_SEARCH: "web_validation_search",
  WEB_VALIDATION_CROSS_CHECK: "web_validation_cross_check",
  WEB_VALIDATION_COMPLETE: "web_validation_complete",

  // Phase 6: Task Generation
  TASK_GENERATION_START: "task_generation_start",
  TASK_GENERATION_PARENT: "task_generation_parent",
  TASK_GENERATION_CHILDREN: "task_generation_children",
  TASK_GENERATION_COMPLETE: "task_generation_complete",

  // Phase 6.5: Iterative Retrieval (sub-phase of task generation)
  THEME_EXTRACTION_START: "theme_extraction_start",
  THEME_EXTRACTION_COMPLETE: "theme_extraction_complete",
  TARGETED_SEARCH_PROGRESS: "targeted_search_progress",
  COMPLEXITY_ANALYSIS_PROGRESS: "complexity_analysis_progress",
  SUBTASK_GENERATION_PROGRESS: "subtask_generation_progress",

  // System Events
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
  EXECUTION_COMPLETE: "execution_complete",
};

/**
 * Phase status enum
 */
export const PHASE_STATUS = {
  PENDING: "pending",
  ACTIVE: "active",
  COMPLETE: "complete",
  ERROR: "error",
};

/**
 * Parse a single SSE event
 * @param {string} eventData - Raw event data from SSE
 * @returns {object|null} Parsed event object or null if invalid
 */
export function parseEvent(eventData) {
  if (!eventData || !eventData.trim()) {
    return null;
  }

  try {
    // SSE format: "data: {json}\n\n"
    // Strip "data: " prefix if present
    const jsonString = eventData.replace(/^data:\s*/, "").trim();

    if (!jsonString) {
      return null;
    }

    const event = JSON.parse(jsonString);

    // Validate required fields
    if (!event.event_type || !event.phase) {
      console.warn("Invalid event: missing event_type or phase", event);
      return null;
    }

    return event;
  } catch (error) {
    console.error("Error parsing SSE event:", error, eventData);
    return null;
  }
}

/**
 * Initialize phase state structure
 * @returns {object} Initial phase state
 */
export function initializePhaseState() {
  const phaseState = {};

  Object.keys(PHASES).forEach((phaseId) => {
    phaseState[phaseId] = {
      status: PHASE_STATUS.PENDING,
      progress: 0,
      message: "",
      data: {},
      error: null,
      startTime: null,
      endTime: null,
    };
  });

  return phaseState;
}

const cleanString = (str) => {
  return str
    ? str
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "")
    : "";
};

/**
 * Update phase state based on event
 * @param {object} phaseState - Current phase state
 * @param {object} event - Parsed event
 * @returns {object} Updated phase state
 */
export function updatePhaseState(phaseState, event) {
  const { phase, event_type, progress, message, data, error, timestamp } = event;

  // Create a deep copy to avoid mutations
  const newPhaseState = JSON.parse(JSON.stringify(phaseState));

  // Get current phase
  const currentPhase = newPhaseState[phase];

  if (!currentPhase) {
    console.warn("Unknown phase:", phase);
    return phaseState;
  }

  // Explicit start and completion event mappings per phase to prevent sub-steps
  // (like topic_decompose_complete or agent_complete) from prematurely completing the phase.
  const phaseStartEvents = {
    1: ["decomposition_start"],
    2: ["search_start"],
    3: ["rerank_start"],
    4: ["analysis_start", "comprehensive_retrieval_start", "topic_decompose_start"],
    5: ["executive_summary_start"],
    6: ["web_validation_start"],
    7: ["task_generation_start", "theme_extraction_start"],
  };

  const phaseCompletionEvents = {
    1: ["decomposition_complete"],
    2: ["search_all_complete"],
    3: ["rerank_complete"],
    4: ["analysis_complete", "execution_complete"],
    5: ["executive_summary_complete"],
    6: ["web_validation_complete"],
    7: ["task_generation_complete", "execution_complete"],
  };

  const starts = phaseStartEvents[phase] || [];
  const completes = phaseCompletionEvents[phase] || [];

  // Update status based on event type
  if (starts.includes(event_type) || (starts.length === 0 && event_type.includes("_start"))) {
    currentPhase.status = PHASE_STATUS.ACTIVE;
    if (!currentPhase.startTime) {
      currentPhase.startTime = timestamp;
    }
  } else if (
    completes.includes(event_type) ||
    (completes.length === 0 && event_type.includes("_complete"))
  ) {
    // Only transition to complete if it is a major phase completion event
    const isSubComplete = [
      "topic_decompose_complete",
      "topic_assemble_complete",
      "topic_agent_complete",
      "agent_complete",
      "parallel_agents_complete",
      "comprehensive_retrieval_complete",
      "completeness_validation_complete",
      "refinement_complete",
      "theme_extraction_complete",
    ].includes(event_type);

    if (!isSubComplete) {
      currentPhase.status = PHASE_STATUS.COMPLETE;
      currentPhase.progress = 100;
      currentPhase.endTime = timestamp;
    }
  } else if (event_type === EVENT_TYPES.ERROR) {
    currentPhase.status = PHASE_STATUS.ERROR;
    currentPhase.error = error;
  }

  // Update progress
  if (progress !== undefined && progress !== null) {
    currentPhase.progress = Math.min(100, Math.max(0, progress));
  }

  // Update message
  if (message) {
    currentPhase.message = message;
  }

  // Merge data
  if (data && Object.keys(data).length > 0) {
    currentPhase.data = {
      ...currentPhase.data,
      ...data,
    };
  }

  // Aggregate agent and topic states in Phase 4
  if (phase === 4) {
    if (!currentPhase.data) {
      currentPhase.data = {};
    }

    if (event_type === "comprehensive_retrieval_start") {
      currentPhase.data.retrievalStatus = "running";
      currentPhase.data.targetDocs = data?.target_docs;
    } else if (event_type === "comprehensive_retrieval_complete") {
      currentPhase.data.retrievalStatus = "complete";
      currentPhase.data.retrievedDocs = data?.retrieved_docs;
    } else if (event_type === "topic_decompose_start") {
      currentPhase.data.decompositionStatus = "running";
    } else if (event_type === "topic_decompose_complete") {
      currentPhase.data.decompositionStatus = "complete";
      currentPhase.data.topicCount = data?.topic_count;
      currentPhase.data.shouldUseTopicMode = data?.should_use_topic_mode;
      currentPhase.data.topics = {};
      if (data?.topics) {
        data.topics.forEach((topicLabel, idx) => {
          const topicId = `topic_${idx}`;
          currentPhase.data.topics[topicId] = {
            id: topicId,
            label: topicLabel,
            status: "pending",
          };
        });
      }
    } else if (event_type === "parallel_agents_start") {
      currentPhase.data.agents = {};
      currentPhase.data.shouldUseTopicMode = false;
      if (data?.agents) {
        data.agents.forEach((agentName) => {
          currentPhase.data.agents[agentName] = {
            status: "pending",
            label: agentName.charAt(0).toUpperCase() + agentName.slice(1) + " Agent",
          };
        });
      }
    } else if (event_type === "agent_start" && data?.agent) {
      if (!currentPhase.data.agents) {
        currentPhase.data.agents = {};
      }
      currentPhase.data.agents[data.agent] = {
        ...currentPhase.data.agents[data.agent],
        status: "running",
        label: data.label || currentPhase.data.agents[data.agent]?.label || data.agent,
        startTime: timestamp,
      };
    } else if (event_type === "agent_complete" && data?.agent) {
      if (!currentPhase.data.agents) {
        currentPhase.data.agents = {};
      }
      currentPhase.data.agents[data.agent] = {
        ...currentPhase.data.agents[data.agent],
        status: "complete",
        label: data.label || currentPhase.data.agents[data.agent]?.label || data.agent,
        endTime: timestamp,
        chars: data.chars,
      };
    } else if (event_type === "topic_agent_start" && data?.topic_id) {
      if (!currentPhase.data.topics) {
        currentPhase.data.topics = {};
      }
      const existingKey = Object.keys(currentPhase.data.topics).find((key) => {
        const item = currentPhase.data.topics[key];
        const cleanItemLabel = cleanString(item.label);
        const cleanEventLabel = cleanString(data.topic_label);
        const cleanEventId = cleanString(data.topic_id);
        return cleanItemLabel === cleanEventLabel || cleanItemLabel === cleanEventId;
      });
      const keyToUpdate = existingKey || data.topic_id;
      currentPhase.data.topics[keyToUpdate] = {
        ...currentPhase.data.topics[keyToUpdate],
        status: "running",
        label: data.topic_label || currentPhase.data.topics[keyToUpdate]?.label || data.topic_id,
        startTime: timestamp,
      };
      currentPhase.data.shouldUseTopicMode = true;
    } else if (event_type === "topic_agent_retry" && data?.topic_id) {
      if (!currentPhase.data.topics) {
        currentPhase.data.topics = {};
      }
      const existingKey = Object.keys(currentPhase.data.topics).find((key) => {
        const item = currentPhase.data.topics[key];
        const cleanItemLabel = cleanString(item.label);
        const cleanEventLabel = cleanString(data.topic_label);
        const cleanEventId = cleanString(data.topic_id);
        return cleanItemLabel === cleanEventLabel || cleanItemLabel === cleanEventId;
      });
      const keyToUpdate = existingKey || data.topic_id;
      currentPhase.data.topics[keyToUpdate] = {
        ...currentPhase.data.topics[keyToUpdate],
        status: "retrying",
        judgeScore: data.judge_score,
        gaps: data.gaps,
      };
    } else if (event_type === "topic_agent_complete" && data?.topic_id) {
      if (!currentPhase.data.topics) {
        currentPhase.data.topics = {};
      }
      const existingKey = Object.keys(currentPhase.data.topics).find((key) => {
        const item = currentPhase.data.topics[key];
        const cleanItemLabel = cleanString(item.label);
        const cleanEventLabel = cleanString(data.topic_label);
        const cleanEventId = cleanString(data.topic_id);
        return cleanItemLabel === cleanEventLabel || cleanItemLabel === cleanEventId;
      });
      const keyToUpdate = existingKey || data.topic_id;
      currentPhase.data.topics[keyToUpdate] = {
        ...currentPhase.data.topics[keyToUpdate],
        status: "complete",
        endTime: timestamp,
        chars: data.chars,
        judgeScore: data.judge_score,
        retryCount: data.retry_count,
      };
    } else if (event_type === "topic_assemble_start") {
      currentPhase.data.assemblyStatus = "running";
    } else if (event_type === "topic_assemble_complete") {
      currentPhase.data.assemblyStatus = "complete";
      currentPhase.data.totalAssembledChars = data.total_chars;
    } else if (event_type === "completeness_validation_start") {
      currentPhase.data.validationStatus = "running";
      currentPhase.data.validationThreshold = data.threshold;
    } else if (event_type === "completeness_validation_complete") {
      currentPhase.data.validationStatus = "complete";
      currentPhase.data.completenessScore = data.score;
      currentPhase.data.isComplete = data.is_complete;
      currentPhase.data.dimensionScores = data.dimension_scores;
    }

    // Calculate dynamic progress if backend did not send it
    if (progress === undefined || progress === null) {
      if (currentPhase.data.topics && Object.keys(currentPhase.data.topics).length > 0) {
        const topicList = Object.values(currentPhase.data.topics);
        const total = topicList.length;
        const completed = topicList.filter((t) => t.status === "complete").length;
        let phaseProgress = 10; // decomposition complete
        phaseProgress += (completed / total) * 70; // topic agents are 70%
        if (currentPhase.data.assemblyStatus === "complete") {
          phaseProgress += 10;
        }
        if (currentPhase.data.validationStatus === "complete") {
          phaseProgress += 10;
        }
        currentPhase.progress = Math.min(98, phaseProgress);
      } else if (currentPhase.data.agents && Object.keys(currentPhase.data.agents).length > 0) {
        const agentList = Object.values(currentPhase.data.agents);
        const total = agentList.length;
        const completed = agentList.filter((a) => a.status === "complete").length;
        currentPhase.progress = Math.min(98, (completed / total) * 90);
      }
    }
  }

  return newPhaseState;
}

/**
 * Extract progress log tokens (phases 1-4 — shown in log/status panel)
 * @param {object} event - Parsed event
 * @returns {string|null} Token text or null
 */
export function extractProgressToken(event) {
  if (event.event_type === EVENT_TYPES.PROGRESS_TOKEN && event.data?.token) {
    return event.data.token;
  }
  return null;
}

/**
 * Extract streaming text tokens from analysis phase
 * @param {object} event - Parsed event
 * @returns {string|null} Token text or null
 */
export function extractAnalysisToken(event) {
  if (event.event_type === EVENT_TYPES.ANALYSIS_TOKEN && event.data?.token) {
    return event.data.token;
  }
  return null;
}

/**
 * Extract streaming text tokens from executive summary phase
 * @param {object} event - Parsed event
 * @returns {string|null} Token text or null
 */
export function extractExecutiveSummaryToken(event) {
  if (event.event_type === EVENT_TYPES.EXECUTIVE_SUMMARY_TOKEN && event.data?.token) {
    return event.data.token;
  }
  return null;
}

/**
 * Check if execution is complete
 * @param {object} event - Parsed event
 * @returns {boolean} True if execution complete
 */
export function isExecutionComplete(event) {
  return event.event_type === EVENT_TYPES.EXECUTION_COMPLETE;
}

/**
 * Get current active phase
 * @param {object} phaseState - Phase state
 * @returns {number|null} Active phase ID or null
 */
export function getActivePhase(phaseState) {
  for (const [phaseId, phase] of Object.entries(phaseState)) {
    if (phase.status === PHASE_STATUS.ACTIVE) {
      return parseFloat(phaseId);
    }
  }
  return null;
}

/**
 * Calculate overall execution progress
 * @param {object} phaseState - Phase state
 * @returns {number} Progress percentage (0-100)
 */
export function calculateOverallProgress(phaseState) {
  const phases = Object.values(phaseState);
  const totalProgress = phases.reduce((sum, phase) => sum + phase.progress, 0);
  return Math.round(totalProgress / phases.length);
}

/**
 * Format duration for display
 * @param {string} startTime - ISO timestamp
 * @param {string} endTime - ISO timestamp
 * @returns {string} Formatted duration
 */
export function formatDuration(startTime, endTime) {
  if (!startTime) {
    return "-";
  }

  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const durationMs = end - start;

  if (durationMs < 1000) {
    return `${durationMs}ms`;
  } else if (durationMs < 60000) {
    return `${(durationMs / 1000).toFixed(1)}s`;
  } else {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }
}

/**
 * Export execution log as text
 * @param {object} phaseState - Phase state
 * @param {array} events - All events
 * @returns {string} Formatted log text
 */
export function exportExecutionLog(phaseState, events) {
  let log = "Workflow Execution Log\n";
  log += "=".repeat(80) + "\n";
  log += `Generated at: ${new Date().toISOString()}\n\n`;

  // Phase summaries
  Object.entries(phaseState).forEach(([phaseId, phase]) => {
    const phaseInfo = PHASES[phaseId];
    log += `\nPhase ${phaseId}: ${phaseInfo.name}\n`;
    log += "-".repeat(80) + "\n";
    log += `Status: ${phase.status}\n`;
    log += `Progress: ${phase.progress}%\n`;
    log += `Duration: ${formatDuration(phase.startTime, phase.endTime)}\n`;

    if (phase.message) {
      log += `Message: ${phase.message}\n`;
    }

    if (phase.error) {
      log += `Error: ${phase.error}\n`;
    }

    log += "\n";
  });

  // Detailed event log
  log += "\n" + "=".repeat(80) + "\n";
  log += "Detailed Event Log\n";
  log += "=".repeat(80) + "\n\n";

  events.forEach((event, index) => {
    log += `[${index + 1}] ${event.timestamp || "N/A"}\n`;
    log += `Phase ${event.phase}: ${event.event_type}\n`;

    if (event.message) {
      log += `Message: ${event.message}\n`;
    }

    if (event.data && Object.keys(event.data).length > 0) {
      log += `Data: ${JSON.stringify(event.data, null, 2)}\n`;
    }

    log += "\n";
  });

  return log;
}

/**
 * Validate SSE connection readiness
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number
 * @param {string} userQuery - User query
 * @returns {object} Validation result with { valid, errors }
 */
export function validateStreamingRequest(workflowId, stageNumber, userQuery) {
  const errors = [];

  if (!workflowId || typeof workflowId !== "string") {
    errors.push("Invalid workflow ID");
  }

  if (!stageNumber || typeof stageNumber !== "number" || stageNumber < 1) {
    errors.push("Invalid stage number");
  }

  if (!userQuery || typeof userQuery !== "string" || !userQuery.trim()) {
    errors.push("User query is required");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

const streamingEventParser = {
  PHASES,
  EVENT_TYPES,
  PHASE_STATUS,
  parseEvent,
  initializePhaseState,
  updatePhaseState,
  extractProgressToken,
  extractAnalysisToken,
  extractExecutiveSummaryToken,
  isExecutionComplete,
  getActivePhase,
  calculateOverallProgress,
  formatDuration,
  exportExecutionLog,
  validateStreamingRequest,
};

export default streamingEventParser;
